/**
 * Supabase Client — Session Persistence Fix
 * 
 * Requirements:
 * - Uses @supabase/ssr's createBrowserClient as the single source of truth for auth state
 * - Cookies are the shared storage between middleware (createServerClient) and client (createBrowserClient)
 * - Eliminates the previous dual-storage race condition (localStorage vs cookies divergence)
 * - Consistent base64url encoding and 3180-byte chunking with the middleware
 * - Preserves mobile webview detection for session sync coordination
 * 
 * Previous bug: The old implementation used createClient with custom hybrid storage
 * (localStorage primary + raw JSON cookies). The middleware wrote cookies in base64url format
 * while the client wrote in raw JSON, causing encoding mismatches and refresh token race
 * conditions that resulted in users being logged out when returning to the site.
 * 
 * Fix: Now both middleware and client use @supabase/ssr's cookie-based storage with identical
 * encoding (base64url) and chunking (3180 bytes). Cookies are the single source of truth,
 * so the middleware's token refresh is immediately visible to the client JS.
 */

import { createBrowserClient } from '@supabase/ssr';
import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('client');

// Mobile webview detection (preserved for session sync coordination).
// New app builds pass a single-use handoff code (?handoff=) which
// useMobileSessionSync exchanges for the webview's OWN session, so the native
// app and the browser refresh separate token families. Older builds still pass
// the native token pair via URL params and setSession() — on that legacy path
// both clients rotate ONE family, and the 10-second reuse interval does NOT
// prevent "refresh_token_already_used": it only forgives reuse of the
// immediate parent token. A copy two rotations behind gets a 400 and auth-js
// drops that side's session (#3128 — verified against local GoTrue 2026-08-19).
export const isMobileWebView = typeof window !== 'undefined' && (
  window.location.pathname.startsWith('/mobile/') ||
  new URLSearchParams(window.location.search).get('mobile') === 'true' ||
  new URLSearchParams(window.location.search).get('isMobile') === 'true' ||
  (window as any).__isCheerVueMobile === true
);

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim();
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

/**
 * Create the Supabase browser client using @supabase/ssr.
 * 
 * This uses cookies as the single source of truth — the same storage
 * the middleware's createServerClient uses. Key behaviors set by
 * createBrowserClient (cannot be overridden):
 *   - flowType: 'pkce'            (more secure than previous 'implicit')
 *   - autoRefreshToken: true       (in browser)
 *   - detectSessionInUrl: true     (in browser — handles PKCE callback codes)
 *   - persistSession: true
 *   - storage: document.cookie     (base64url encoded, 3180-byte chunks)
 * 
 * isSingleton: true ensures only one instance exists across the app,
 * equivalent to the previous Symbol-based global singleton pattern.
 */
function createSupabaseBrowserClient(): SupabaseClient<Database, 'public'> {
  if (!supabaseUrl || !supabaseKey) {
    logger.error('CRITICAL: Missing Supabase environment variables. Supabase client cannot be initialized.');
    throw new Error('Supabase URL or Key is missing in environment variables.');
  }

  return createBrowserClient<Database, 'public'>(supabaseUrl, supabaseKey, {
    isSingleton: true,
    global: {
      headers: { 'x-application-name': 'gym-admin-dashboard' },
    },
    realtime: {
      timeout: 60000,
      heartbeatIntervalMs: 30000,
    },
  });
}

// Export the singleton client — used by 150+ files across the app.
export const supabase = createSupabaseBrowserClient();

/**
 * Resets the Supabase client instance.
 * Note: createBrowserClient with isSingleton caches at module level inside @supabase/ssr.
 * A full page reload is the most reliable way to get a fresh instance.
 */
export function resetSupabase(): void {
  logger.warn("resetSupabase called. A full page reload is necessary to get a new client instance.");
}

/**
 * Checks if the user is authenticated.
 * Reads from the cookie-based session (shared with middleware).
 */
export async function checkAuthStatus(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  } catch (error) {
    logger.error('Error checking auth status:', error);
    return false;
  }
}

/**
 * Example usage:
 * 
 * import { supabase } from '@/utils/supabase/client';
 * 
 * // Fetch data
 * const { data, error } = await supabase
 *   .from('athletes')
 *   .select('*')
 *   .eq('team_id', teamId);
 * 
 * // Handle errors
 * if (error) {
 *   logger.error('Error fetching athletes:', error);
 *   return [];
 * }
 * 
 * return data;
 */